import type { BasicFetch } from '../types/basicFetch';
import { concatByteArrays } from '../util/byteArray';
import { fetchByteRange } from './fetchByteRange';
import { inspectMoov } from './inspectMoov';
import type { Mp4Info } from './mp4Types';
import { tryGetMoovBox, type Mp4Box } from './parser';

export async function fetchAndInspectMoov(
  fetch: BasicFetch,
  url: string,
  // If mp4Info is not undefined, the data from the newly fetched moov will
  // be incorporated into it
  mp4Info: Mp4Info | undefined,
  ignoreVideo: boolean,
  ignoreAudio: boolean,
): Promise<Mp4Info> {

  const getBytes = async (offset: number, size: number): Promise<Uint8Array> => {
    return await fetchByteRange(fetch, url, offset, offset + size);
  };
  return inspectMoovBytes(getBytes, url, mp4Info, ignoreVideo, ignoreAudio);
}

export async function inspectMoovBytes(
  getBytes: (offset: number, size: number) => Promise<Uint8Array>,
  url: string,
  mp4Info: Mp4Info | undefined,
  ignoreVideo: boolean,
  ignoreAudio: boolean,
): Promise<Mp4Info> {
  const moov = await readMoovBox(getBytes);
  return inspectMoov(mp4Info, moov, url, ignoreVideo, ignoreAudio);
}

export async function readMoovBox(
  getBytes: (offset: number, size: number) => Promise<Uint8Array>,
): Promise<Mp4Box> {
  let currentStart = 0;
  let bytes = await getBytes(currentStart, 100000);
  while (true) {
    const result = tryGetMoovBox(bytes);
    if (result.type === 'error') {
      throw result.error;
    }
    if (result.type === 'ok') {
      return result.moov;
    }

    if (result.type === 'incomplete-moov-box') {
      const end = result.moovBoxEnd;
      const additionalBytes = await getBytes(currentStart + bytes.length, end - bytes.length);
      if (additionalBytes.length === 0) {
        throw new Error(`Truncated moov box at byte offset ${currentStart}`);
      }
      bytes = concatByteArrays([bytes, additionalBytes]);
    }

    if (result.type === 'moov-not-found') {
      // nextBoxStart is relative to the start of bytes.
      const nextBoxStart = currentStart + result.nextBoxStart;
      bytes = await getBytes(nextBoxStart, 10000);
      currentStart = nextBoxStart;
      if (bytes.length === 0) {
        throw new Error('Reached the end of the file without finding a moov box');
      }
    }
  }
}
